import * as DG from "ui/dialogs"
import { useEffect, useState } from "react"

import { queryCache, useMutation } from "react-query"

import Button from "ui/buttons/Button"
import FormControl from "@material-ui/core/FormControl"
import { TextInput } from "components/inputs/TextInput"
import { css } from "@emotion/css"
import { updateClientAgencyConfigCopyCode } from "api/agencies"
import { useI18n } from "providers/I18n"
import { useSendFeedBack } from "components/FeedbackSnackbar"

export type Props = {
  open: boolean
  onClose: VoidFunction
  configId: string
  copyId?: string
  defaultValue?: string
}

export const UpdateCopyCodeModal = (props: Props) => {
  const { open, onClose, defaultValue = "", configId, copyId = "" } = props
  const { t } = useI18n()

  const [code, setCode] = useState<string>("")
  const [error, setError] = useState<boolean>()
  const feedback = useSendFeedBack()

  const [updateCode, { isLoading }] = useMutation(
    updateClientAgencyConfigCopyCode,
    {
      onSuccess: async () => {
        queryCache.invalidateQueries("client-agency-config-copies")
        onClose()
      },
      onError: (err) => {
        feedback(t(err.message), "error")
      },
    },
  )

  useEffect(() => {
    setCode(defaultValue)
    setError(false)
  }, [defaultValue])

  const onUpdate = (code: string) =>
    updateCode({ clientAgencyConfigId: configId, copyId, externalCode: code })

  const handleChange = (value: string) => {
    setCode(value)
    setError(value.length > 32 || value.includes(" "))
  }

  const handleSubmit = () => {
    onUpdate(code)
  }

  const handleClose = () => {
    setCode("")
    onClose()
  }

  const id = "update-copy-code"

  return (
    <DG.Dialog
      className={styles.dialog}
      open={open || false}
      onClose={handleClose}
      id={id}
      disableEscapeKeyDown
      BackdropProps={{ style: { backgroundColor: "rgba(0, 0, 0, 0.3)" } }}
    >
      <DG.DialogHeader>
        <DG.DialogTitle id={id}>
          <div className={styles.title}>
            <p className={"HeadlineH5BlackBold"}>
              {t("copies.modal.codeUpdate.title")}
            </p>
            <p className={"BodyBody-1BlackLight"}>
              {t("copies.modal.codeUpdate.subtitle")}
            </p>
          </div>
        </DG.DialogTitle>

        <DG.DialogClose onClose={handleClose} />
      </DG.DialogHeader>

      <DG.DialogContent className={styles.content}>
        <div className={styles.inputWrapper}>
          <FormControl variant="outlined">
            <div>
              <TextInput
                label={t("copies.modal.codeUpdate.externalCode")}
                value={code}
                size="medium"
                onChange={(e) => handleChange(e.currentTarget.value)}
                className={styles.input}
                errorMessage={
                  error ? t("copies.modal.codeUpdate.errorMessage") : undefined
                }
              />
            </div>
          </FormControl>
        </div>
      </DG.DialogContent>
      <DG.DialogFooter>
        <DG.DialogActions>
          <Button
            variant="outlined"
            color="primary"
            onClick={handleClose}
            disabled={isLoading}
          >
            {t("copies.modal.codeUpdate.cancel")}
          </Button>
          <Button
            variant="contained"
            color="primary"
            type="submit"
            onClick={handleSubmit}
            disabled={error || isLoading || !code.length}
          >
            {t("copies.modal.codeUpdate.update")}
          </Button>
        </DG.DialogActions>
      </DG.DialogFooter>
    </DG.Dialog>
  )
}

const styles = {
  inputWrapper: css`
    margin-right: auto;
  `,
  input: css`
    min-width: 255px;
    min-height: var(--spacing-xxl-6);
  `,
  content: css`
    padding: var(--spacing-m) 0 var(--spacing-xl) var(--spacing-xl);
  `,
  dialog: css`
    max-width: 551px;
    margin: auto;
  `,
  title: css`
    display: flex;
    flex-direction: column;
  `,
}
